import React, { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Modal, Form, Input, Select, DatePicker, Row, Col, Button, Divider } from 'antd';
import { PlusOutlined, DeleteOutlined } from '@ant-design/icons';
import { Resident } from '@/types/resources/resident';
import { User } from '@/types/users';
import dayjs from 'dayjs';
import UserFormSection from '@/components/common/UserFormSection';
import ContactsFormSection from '@/components/common/ContactsFormSection';
import { BlockApartmentUnitSelector } from '@/components/common/BlockApartmentUnitSelector';
import type { CreateResidentInput } from '@/api/residents';

interface ResidentFormModalProps {
  visible: boolean;
  onCancel: () => void;
  onSubmit: (values: CreateResidentInput) => void;
  initialValues?: Partial<Resident>;
  title: string;
}

const ResidentFormModal: React.FC<ResidentFormModalProps> = ({
  visible,
  onCancel,
  onSubmit,
  initialValues,
  title,
}) => {
  const { t } = useTranslation(['residents', 'common']);
  const [form] = Form.useForm();
  const [isSubmitting, setIsSubmitting] = useState(false);
  
  // State to store selected data from selectors
  const [selectedBlockData, setSelectedBlockData] = useState<{ id: string; name: string } | null>(null);
  const [selectedApartmentUnitData, setSelectedApartmentUnitData] = useState<{ id: string; name: string } | null>(null);
  
  // State to track found user from email lookup
  const [foundUser, setFoundUser] = useState<User | null>(null);

  // Tour functionality removed

  const initialFormValues = useMemo(
    () => ({
      ...initialValues,
      type: initialValues?.type ?? 'owner',
      status: initialValues?.status ?? 'pending',
      moveInDate: initialValues?.moveInDate ? dayjs(initialValues.moveInDate) : dayjs(),
      blockId: initialValues?.block?.id,
      apartmentUnitId: initialValues?.apartmentUnit?.id,
    }),
    [initialValues]
  );

  // Set selected data for existing resident
  React.useEffect(() => {
    if (initialValues?.block) {
      setSelectedBlockData({ 
        id: initialValues.block.id, 
        name: initialValues.block.name 
      });
    }
    if (initialValues?.apartmentUnit) {
      setSelectedApartmentUnitData({ 
        id: initialValues.apartmentUnit.id, 
        name: initialValues.apartmentUnit.number 
      });
    }
  }, [initialValues]);

  // Handle user found from email lookup
  const handleUserFound = (user: User | null) => {
    setFoundUser(user);
  };

  const handleSubmit = async () => {
    try {
      setIsSubmitting(true);
      const values = await form.validateFields();
      
      // Transform the form values to include block and apartment unit data
      const transformedValues: CreateResidentInput = {
        ...values,
        moveInDate: values.moveInDate?.toDate(),
        apartmentUnit: selectedApartmentUnitData ? {
          id: selectedApartmentUnitData.id,
          number: selectedApartmentUnitData.name,
          block: selectedBlockData ? {
            id: selectedBlockData.id,
            name: selectedBlockData.name
          } : undefined
        } : undefined,
        // Include found user information if available
        existingUser: foundUser,
        // Remove form-specific fields
        blockId: undefined,
        apartmentUnitId: undefined,
      };

      await onSubmit(transformedValues);
      form.resetFields();
      
      // Reset selected data and found user
      setSelectedBlockData(null);
      setSelectedApartmentUnitData(null);
      setFoundUser(null);
    } catch (error) {
      console.error('Form validation failed:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Modal
      title={title}
      open={visible}
      onCancel={onCancel}
      onOk={handleSubmit}
      destroyOnHidden
      width={800}
      confirmLoading={isSubmitting}
      maskClosable={!isSubmitting}
      keyboard={!isSubmitting}
      okButtonProps={{ loading: isSubmitting }}
    >
      <Form
        form={form}
        layout="vertical"
        initialValues={initialFormValues}
        preserve={false}
      >
        {/* User Information Section */}
        <div id="user-details-section">
          <UserFormSection 
            form={form} 
            initialValues={initialValues} 
            onUserFound={handleUserFound}
            fieldIds={{
              email: 'resident-email-input',
              displayName: 'resident-name-input',
              phoneNumber: 'resident-phone-input'
            }}
          />
        </div>

        {/* Resident Specific Information */}
        <div id="resident-info-section">
          <Divider>{t('residents:sections.residentInfo')}</Divider>
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                name="type"
                label={t('residents:fields.type')}
                rules={[{ required: true, message: t('common:validation.required') }]}
              >
                <Select id="resident-type-select">
                  <Select.Option value="owner">{t('residents:residencyTypes.owner')}</Select.Option>
                  <Select.Option value="tenant">{t('residents:residencyTypes.tenant')}</Select.Option>
                  <Select.Option value="family_member">{t('residents:residencyTypes.family_member')}</Select.Option>
                  <Select.Option value="other">{t('residents:residencyTypes.other')}</Select.Option>
                </Select>
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                name="status"
                label={t('residents:fields.status')}
                rules={[{ required: true, message: t('common:validation.required') }]}
              >
                <Select id="resident-status-select">
                  <Select.Option value="active">{t('residents:status.active')}</Select.Option>
                  <Select.Option value="inactive">{t('residents:status.inactive')}</Select.Option>
                  <Select.Option value="pending">{t('residents:status.pending')}</Select.Option>
                  <Select.Option value="moved_out">{t('residents:status.moved_out')}</Select.Option>
                </Select>
              </Form.Item>
            </Col>
          </Row>
        </div>

        {/* Block and Apartment Unit Selection */}
        <div id="location-section">
          <Divider>{t('residents:sections.location')}</Divider>
          <div id="resident-block-selector">
            <BlockApartmentUnitSelector
              onBlockDataChange={setSelectedBlockData}
              onApartmentUnitDataChange={setSelectedApartmentUnitData}
              fieldIds={{
                blockId: 'resident-block-select',
                apartmentUnitId: 'resident-apartment-selector'
              }}
            />
          </div>

          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                name="moveInDate"
                label={t('residents:fields.moveInDate')}
                rules={[{ required: true, message: t('common:validation.required') }]}
              >
                <DatePicker id="resident-move-in-date" style={{ width: '100%' }} />
              </Form.Item>
            </Col>
          </Row>
        </div>

        {/* Vehicles */}
        <Form.List name="vehicles">
          {(fields, { add, remove }) => (
            <>
              {fields.map(({ key, name, ...restField }) => (
                <Row gutter={16} key={key} align="middle">
                  <Col span={11}>
                    <Form.Item
                      {...restField}
                      name={[name, 'type']}
                      rules={[{ required: true, message: t('common:validation.required') }]}
                    >
                      <Select placeholder={t('residents:fields.vehicleType')}>
                        <Select.Option value="car">{t('residents:vehicleTypes.car')}</Select.Option>
                        <Select.Option value="motorcycle">{t('residents:vehicleTypes.motorcycle')}</Select.Option>
                        <Select.Option value="bicycle">{t('residents:vehicleTypes.bicycle')}</Select.Option>
                        <Select.Option value="other">{t('residents:vehicleTypes.other')}</Select.Option>
                      </Select>
                    </Form.Item>
                  </Col>
                  <Col span={11}>
                    <Form.Item
                      {...restField}
                      name={[name, 'number']}
                      rules={[{ required: true, message: t('common:validation.required') }]}
                    >
                      <Input placeholder={t('residents:fields.vehicleNumber')} />
                    </Form.Item>
                  </Col>
                  <Col span={2}>
                    <Button type="text" icon={<DeleteOutlined />} onClick={() => remove(name)} />
                  </Col>
                </Row>
              ))}
              <Form.Item>
                <Button type="dashed" onClick={() => add()} block icon={<PlusOutlined />}>
                  {t('residents:actions.addVehicle')}
                </Button>
              </Form.Item>
            </>
          )}
        </Form.List>

        {/* Pets */}
        <Form.List name="pets">
          {(fields, { add, remove }) => (
            <>
              {fields.map(({ key, name, ...restField }) => (
                <Row gutter={16} key={key} align="middle">
                  <Col span={11}>
                    <Form.Item
                      {...restField}
                      name={[name, 'type']}
                      rules={[{ required: true, message: t('common:validation.required') }]}
                    >
                      <Select placeholder={t('residents:fields.petType')}>
                        <Select.Option value="dog">{t('residents:petTypes.dog')}</Select.Option>
                        <Select.Option value="cat">{t('residents:petTypes.cat')}</Select.Option>
                        <Select.Option value="bird">{t('residents:petTypes.bird')}</Select.Option>
                        <Select.Option value="other">{t('residents:petTypes.other')}</Select.Option>
                      </Select>
                    </Form.Item>
                  </Col>
                  <Col span={11}>
                    <Form.Item
                      {...restField}
                      name={[name, 'name']}
                      rules={[{ required: true, message: t('common:validation.required') }]}
                    >
                      <Input placeholder={t('residents:fields.petName')} />
                    </Form.Item>
                  </Col>
                  <Col span={2}>
                    <Button type="text" icon={<DeleteOutlined />} onClick={() => remove(name)} />
                  </Col>
                </Row>
              ))}
              <Form.Item>
                <Button type="dashed" onClick={() => add()} block icon={<PlusOutlined />}>
                  {t('residents:actions.addPet')}
                </Button>
              </Form.Item>
            </>
          )}
        </Form.List>

        {/* Family Members */}
        <Form.List name="familyMembers">
          {(fields, { add, remove }) => (
            <>
              {fields.map(({ key, name, ...restField }) => (
                <Row gutter={16} key={key} align="middle">
                  <Col span={8}>
                    <Form.Item
                      {...restField}
                      name={[name, 'name']}
                      rules={[{ required: true, message: t('common:validation.required') }]}
                    >
                      <Input placeholder={t('residents:fields.familyMemberName')} />
                    </Form.Item>
                  </Col>
                  <Col span={8}>
                    <Form.Item
                      {...restField}
                      name={[name, 'relation']}
                      rules={[{ required: true, message: t('common:validation.required') }]}
                    >
                      <Select placeholder={t('residents:fields.relation')}>
                        <Select.Option value="spouse">{t('residents:fields.relations.spouse')}</Select.Option>
                        <Select.Option value="child">{t('residents:fields.relations.child')}</Select.Option>
                        <Select.Option value="parent">{t('residents:fields.relations.parent')}</Select.Option>
                        <Select.Option value="sibling">{t('residents:fields.relations.sibling')}</Select.Option>
                        <Select.Option value="other">{t('residents:fields.relations.other')}</Select.Option>
                      </Select>
                    </Form.Item>
                  </Col>
                  <Col span={6}>
                    <Form.Item
                      {...restField}
                      name={[name, 'phone']}
                    >
                      <Input placeholder={t('residents:fields.phone')} />
                    </Form.Item>
                  </Col>
                  <Col span={2}>
                    <Button type="text" icon={<DeleteOutlined />} onClick={() => remove(name)} />
                  </Col>
                </Row>
              ))}
              <Form.Item>
                <Button type="dashed" onClick={() => add()} block icon={<PlusOutlined />}>
                  {t('residents:actions.addFamilyMember')}
                </Button>
              </Form.Item>
            </>
          )}
        </Form.List>

        {/* Contacts */}
        <Form.Item>
          <ContactsFormSection t={t} />
        </Form.Item>

        {/* Notes */}
        <Divider>{t('residents:sections.notes')}</Divider>
        <Row gutter={16}>
          <Col span={24}>
            <Form.Item
              name="notes"
            >
              <Input.TextArea rows={4} />
            </Form.Item>
          </Col>
        </Row>
      </Form>
    </Modal>
  );
};

export default ResidentFormModal; 